import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocalOffer as PackageIcon
} from '@mui/icons-material';
import PackageService from '../../services/PackageService';

const PackageForm = ({ package: editingPackage, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    totalSessions: '',
    freeSessions: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (editingPackage) {
      setFormData({
        name: editingPackage.packageName || '',
        description: editingPackage.description || '',
        price: editingPackage.price?.toString() || '',
        totalSessions: editingPackage.includedSessions?.toString() || '',
        freeSessions: editingPackage.freeSessions?.toString() || '',
        isActive: editingPackage.isActive !== false
      });
    }
  }, [editingPackage]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Package name is required';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }

    if (!formData.totalSessions || parseInt(formData.totalSessions) <= 0) {
      errors.totalSessions = 'Total sessions must be greater than 0';
    }

    if (formData.freeSessions && parseInt(formData.freeSessions) < 0) {
      errors.freeSessions = 'Free sessions cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const packageData = {
        packageName: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        includedSessions: parseInt(formData.totalSessions),
        freeSessions: parseInt(formData.freeSessions) || 0,
        isActive: formData.isActive
      };

      let savedPackage;
      if (editingPackage) {
        savedPackage = await PackageService.updatePackage(editingPackage.packageId, {
          ...packageData,
          packageId: editingPackage.packageId
        });
      } else {
        savedPackage = await PackageService.createPackage(packageData);
      }

      onSave(savedPackage);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save package');
      console.error('Error saving package:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateValuePerSession = () => {
    const price = parseFloat(formData.price) || 0;
    const totalSessions = parseInt(formData.totalSessions) || 0;
    const freeSessions = parseInt(formData.freeSessions) || 0;
    const allSessions = totalSessions + freeSessions;
    
    if (allSessions > 0) {
      return (price / allSessions).toFixed(2);
    }
    return '0.00';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <PackageIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          {editingPackage ? 'Edit Package' : 'Create New Package'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Package Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Package Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      error={!!validationErrors.name}
                      helperText={validationErrors.name}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      multiline
                      rows={3}
                      placeholder="Describe what this package includes..."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price (MYR)"
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      error={!!validationErrors.price}
                      helperText={validationErrors.price}
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Total Sessions"
                      type="number"
                      value={formData.totalSessions}
                      onChange={(e) => handleInputChange('totalSessions', e.target.value)}
                      error={!!validationErrors.totalSessions}
                      helperText={validationErrors.totalSessions}
                      inputProps={{ min: 1 }}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Free Sessions"
                      type="number"
                      value={formData.freeSessions}
                      onChange={(e) => handleInputChange('freeSessions', e.target.value)}
                      error={!!validationErrors.freeSessions}
                      helperText={validationErrors.freeSessions || 'Additional free sessions included'}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Active Package"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Package Summary
                </Typography>
                
                <Box sx={{ space: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Price:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formData.price ? formatCurrency(parseFloat(formData.price)) : 'MYR 0.00'}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Paid Sessions:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formData.totalSessions || 0}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Free Sessions:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formData.freeSessions || 0}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Total Sessions:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary.main">
                      {(parseInt(formData.totalSessions) || 0) + (parseInt(formData.freeSessions) || 0)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Value per Session:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="secondary.main">
                      {formatCurrency(parseFloat(calculateValuePerSession()))}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Customers get great value with {formData.freeSessions || 0} free sessions included!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" gap={2} mt={3}>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingPackage ? 'Update Package' : 'Create Package')}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PackageForm;
